import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_TIMETABLE, GENERATE_AI_TIMETABLE, MY_SUBJECTS, MY_EXAMS, MY_ACADEMIC_HEALTH } from '../graphql';
import { Zap, Calendar, Clock, AlertTriangle, BookOpen, CheckCircle, TrendingDown, Star } from 'lucide-react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TYPE_COLORS = {
  lecture: '#4F46E5', lab: '#059669', study: '#D97706',
  club: '#0891B2', personal: '#7C3AED', break: '#9CA3AF',
};

const TODAY_DAY = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()];

function parseTime(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function hoursUntil(t) {
  const now = new Date();
  const [h, m] = t.split(':').map(Number);
  const target = new Date(now); target.setHours(h, m, 0, 0);
  return Math.max(0, (target - now) / 3600000);
}

export default function TimetablePage() {
  const { data, loading } = useQuery(MY_TIMETABLE);
  const { data: subData } = useQuery(MY_SUBJECTS);
  const { data: examData } = useQuery(MY_EXAMS);
  const { data: healthData } = useQuery(MY_ACADEMIC_HEALTH);
  const [generateTimetable, { loading: generating }] = useMutation(GENERATE_AI_TIMETABLE, { refetchQueries: [MY_TIMETABLE] });
  const [showAiForm, setShowAiForm] = useState(false);
  const [aiForm, setAiForm] = useState({ rawSchedule: '', constraints: '' });
  const [activeTab, setActiveTab] = useState('week'); // 'week' | 'today' | 'smart'

  const handleGenerate = async (e) => {
    e.preventDefault();
    await generateTimetable({ variables: aiForm });
    setShowAiForm(false);
  };

  const timetable = data?.myTimetable;
  const slots = timetable?.slots || [];
  const subjects = subData?.mySubjects || [];
  const exams = examData?.myExams || [];
  const health = healthData?.myAcademicHealth;

  // Group by day
  const byDay = DAYS.reduce((acc, day) => {
    acc[day] = slots.filter(s => s.day === day).sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime));
    return acc;
  }, {});

  // Today's classes
  const todaySlots = byDay[TODAY_DAY] || [];

  // Attendance impact: for each lecture today, show which subject and how much attendance changes
  const attendanceImpact = useMemo(() => {
    return todaySlots.filter(s => s.type === 'lecture' || s.type === 'lab').map(slot => {
      const sub = subjects.find(s => s.name.toLowerCase().includes(slot.title.toLowerCase()) || slot.title.toLowerCase().includes(s.name.toLowerCase()));
      if (!sub) return { slot, sub: null };
      const newTotal = sub.totalClasses + 1;
      const newPctPresent = ((sub.attendedClasses + 1) * 100) / newTotal;
      const newPctAbsent  = (sub.attendedClasses * 100) / newTotal;
      const delta = newPctPresent - sub.percentage;
      return { slot, sub, newPctPresent: newPctPresent.toFixed(1), newPctAbsent: newPctAbsent.toFixed(1), delta: delta.toFixed(1), currentPct: sub.percentage.toFixed(1) };
    });
  }, [todaySlots, subjects]);

  // Upcoming exams (next 14 days)
  const upcomingExams = exams.filter(e => e.daysLeft <= 14).slice(0, 4);

  // Study block suggestions
  const studySuggestions = useMemo(() => {
    const suggestions = [];
    // Subjects in danger → recommend study blocks
    if (health?.subjectHealth) {
      health.subjectHealth.filter(h => h.status === 'DANGER' || h.status === 'DETAINED').forEach(h => {
        suggestions.push({ type: 'attendance', subject: h.subject.name, msg: `${h.subject.name} is at ${h.subject.percentage.toFixed(1)}% — add a study block to prepare for classes`, priority: 'high' });
      });
    }
    // Exams coming up → suggest study time
    upcomingExams.forEach(e => {
      suggestions.push({ type: 'exam', subject: e.subject, msg: `${e.title} is in ${e.daysLeft} day${e.daysLeft !== 1 ? 's' : ''} — add 2h study block daily`, priority: e.daysLeft <= 3 ? 'high' : 'medium' });
    });
    // CGPA low
    if (health?.cgpa > 0 && health?.cgpa < 6.5) {
      suggestions.push({ type: 'cgpa', subject: null, msg: `CGPA ${health.cgpa.toFixed(2)} — add dedicated revision blocks to boost performance`, priority: 'medium' });
    }
    // Free time today → suggest study
    const freeAfterLast = todaySlots.length > 0 && hoursUntil(todaySlots[todaySlots.length - 1]?.endTime || '18:00') > 0;
    if (freeAfterLast && upcomingExams.length > 0) {
      suggestions.push({ type: 'free', subject: null, msg: `You have free time after classes today — great for a ${upcomingExams[0].subject} revision session`, priority: 'low' });
    }
    return suggestions;
  }, [health, upcomingExams, todaySlots]);

  const TABS = [
    { id: 'week',  label: 'Weekly View' },
    { id: 'today', label: `Today (${TODAY_DAY})` },
    { id: 'smart', label: 'Smart Insights' },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Timetable</h1>
          <p className="text-gray-500 text-sm mt-1">Smart schedule with attendance impact & exam integration</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowAiForm(!showAiForm)}>
          <Zap size={18} /> Generate with AI
        </button>
      </div>

      {showAiForm && (
        <form onSubmit={handleGenerate} className="card mb-6 space-y-4">
          <h3 className="font-semibold">Describe Your Schedule</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Schedule (paste anything)</label>
            <textarea className="input !h-40 resize-none" required value={aiForm.rawSchedule}
              onChange={e => setAiForm({...aiForm, rawSchedule: e.target.value})}
              placeholder={`Mon/Wed/Fri: Data Structures 9-10am (Room 101)\nTue/Thu: OS lecture 11am-12pm\nWednesday: DBMS Lab 2-4pm\nFriday: Technical Club 4-5pm`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Constraints / Preferences (optional)</label>
            <input className="input" value={aiForm.constraints}
              onChange={e => setAiForm({...aiForm, constraints: e.target.value})}
              placeholder="No classes before 9am, add study blocks before exams, include gym time" />
          </div>
          <div className="flex gap-3">
            <button className="btn-primary flex items-center gap-2" type="submit" disabled={generating}>
              {generating ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Building...</> : <><Zap size={18} /> Generate</>}
            </button>
            <button className="btn-secondary" type="button" onClick={() => setShowAiForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Exam countdown strip */}
      {upcomingExams.length > 0 && (
        <div className="flex gap-3 mb-5 overflow-x-auto pb-1">
          {upcomingExams.map(e => (
            <div key={e.id} className={`flex-shrink-0 px-4 py-2 rounded-xl border text-sm font-medium flex items-center gap-2 ${
              e.daysLeft <= 3 ? 'bg-red-50 border-red-200 text-red-700' :
              e.daysLeft <= 7 ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
              'bg-blue-50 border-blue-200 text-blue-700'}`}>
              <Clock size={14} />
              <span>{e.title}</span>
              <span className="font-bold">{e.daysLeft}d</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      {slots.length > 0 && (
        <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" /></div>
      ) : slots.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Calendar size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium mb-1">No timetable yet</p>
          <p className="text-sm">Describe your class schedule and let AI build your weekly plan.</p>
        </div>
      ) : activeTab === 'week' ? (
        <>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-4">
            {Object.entries(TYPE_COLORS).map(([type, color]) => (
              <div key={type} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs text-gray-500 capitalize">{type}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DAYS.map(day => (
              <div key={day} className={`card !p-4 ${day === TODAY_DAY ? 'ring-2 ring-indigo-400' : ''}`}>
                <h3 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                  {day}
                  {day === TODAY_DAY && <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Today</span>}
                </h3>
                <div className="space-y-2">
                  {byDay[day].length === 0 ? (
                    <p className="text-xs text-gray-300">Free day</p>
                  ) : byDay[day].map(slot => (
                    <div key={slot.id} className="flex gap-2 items-start">
                      <div className="w-1 rounded-full flex-shrink-0 mt-1"
                        style={{ backgroundColor: slot.color || TYPE_COLORS[slot.type] || '#4F46E5', height: '40px' }} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{slot.title}</p>
                        <p className="text-xs text-gray-400">{slot.startTime}–{slot.endTime}
                          {slot.location && ` · ${slot.location}`}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : activeTab === 'today' ? (
        <div className="space-y-4">
          {todaySlots.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CheckCircle size={40} className="mx-auto mb-2 opacity-30" />
              <p className="font-medium">No classes today!</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-2">{todaySlots.length} class{todaySlots.length !== 1 ? 'es' : ''} today</p>
              {todaySlots.map(slot => {
                const impact = attendanceImpact.find(a => a.slot.id === slot.id);
                const isLecture = slot.type === 'lecture' || slot.type === 'lab';
                return (
                  <div key={slot.id} className="card">
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 rounded-full self-stretch" style={{ backgroundColor: slot.color || TYPE_COLORS[slot.type] || '#4F46E5', minHeight: '48px' }} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-gray-900">{slot.title}</p>
                          <span className="text-xs text-gray-400">{slot.startTime}–{slot.endTime}</span>
                        </div>
                        <p className="text-xs text-gray-500 capitalize">{slot.type}{slot.location && ` · ${slot.location}`}</p>

                        {/* Attendance impact */}
                        {isLecture && impact?.sub && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <div className="bg-green-50 rounded-lg px-3 py-2 text-center">
                              <p className="text-[10px] text-green-600 font-medium mb-0.5">If you attend</p>
                              <p className="text-sm font-bold text-green-700">{impact.newPctPresent}%</p>
                              <p className="text-[10px] text-green-500">+{impact.delta}%</p>
                            </div>
                            <div className="bg-red-50 rounded-lg px-3 py-2 text-center">
                              <p className="text-[10px] text-red-600 font-medium mb-0.5">If you miss</p>
                              <p className="text-sm font-bold text-red-700">{impact.newPctAbsent}%</p>
                              <p className="text-[10px] text-red-500">was {impact.currentPct}%</p>
                            </div>
                          </div>
                        )}
                        {isLecture && !impact?.sub && (
                          <p className="text-xs text-gray-400 mt-1">Link to a subject in Attendance to see impact</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      ) : (
        /* Smart Insights tab */
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900">Personalized Suggestions</h2>

          {studySuggestions.length === 0 && (
            <div className="card text-center py-8 text-gray-400">
              <Star size={36} className="mx-auto mb-2 opacity-30" />
              <p>All good! No urgent suggestions right now.</p>
            </div>
          )}

          {studySuggestions.map((s, i) => (
            <div key={i} className={`card flex items-start gap-3 ${
              s.priority === 'high' ? 'border-l-4 border-red-400' :
              s.priority === 'medium' ? 'border-l-4 border-yellow-400' : 'border-l-4 border-green-400'}`}>
              {s.type === 'exam'       ? <Clock size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
               : s.type === 'attendance' ? <TrendingDown size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
               : s.type === 'cgpa'      ? <AlertTriangle size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
               : <BookOpen size={20} className="text-green-500 flex-shrink-0 mt-0.5" />}
              <div>
                {s.subject && <p className="text-xs font-semibold text-gray-500 mb-0.5">{s.subject}</p>}
                <p className="text-sm text-gray-800">{s.msg}</p>
              </div>
            </div>
          ))}

          {/* Upcoming assignments row */}
          {upcomingExams.length > 0 && (
            <>
              <h3 className="font-semibold text-gray-900 mt-4">Exam Prep Timeline</h3>
              {upcomingExams.map(e => (
                <div key={e.id} className="card">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">{e.title}</p>
                    <span className={`badge ${e.daysLeft <= 3 ? 'bg-red-100 text-red-700' : e.daysLeft <= 7 ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                      {e.daysLeft}d left
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{e.subject}</p>
                  {/* Simple study plan bar */}
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div className={`h-2 rounded-full ${e.daysLeft <= 3 ? 'bg-red-400' : e.daysLeft <= 7 ? 'bg-yellow-400' : 'bg-blue-400'}`}
                      style={{ width: `${Math.max(5, Math.min(100, 100 - (e.daysLeft / 14) * 100))}%` }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Recommended: {e.daysLeft <= 3 ? '4h' : e.daysLeft <= 7 ? '2h' : '1h'}/day revision</p>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
